import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { toast } from 'sonner';
import { 
  Filter, Download, Search, Users, FileSpreadsheet, FileText, 
  Plus, Trash2, ArrowUpDown, ChevronLeft, ChevronRight, X
} from 'lucide-react';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const GENDERS = ['Male', 'Female'];
const MISAQ_STATUSES = ['Done', 'Not Done', 'Under Age'];
const MARITAL_STATUSES = ['Married', 'Single', 'Divorced', 'Widowed'];
const OCCUPATION_TYPES = ['Business', 'Service', 'Student', 'Housewife', 'Retired', 'Other'];
const SUB_OCCUPATION_TYPES = {
  'Business': ['Retail', 'Wholesale', 'Manufacturing', 'Trading', 'Real Estate', 'Other'],
  'Service': ['Government', 'Private', 'IT/Software', 'Banking', 'Healthcare', 'Education', 'Other'],
  'Student': ['School', 'College', 'University', 'Professional Course', 'Other'],
  'Housewife': [],
  'Retired': ['Govt Pensioner', 'Private Pensioner', 'Other'],
  'Other': ['Self Employed', 'Freelancer', 'Unemployed', 'Other']
};

const MOHALLA_ORDER = ['QUTBI MOHALLA', 'NAJMI MOHALLA', 'FAKHRI MOHALLA', 'FATEMI MOHALLA', 'HASANI MOHALLA', 'HASHMI MOHALLA'];

const getMohallaSortKey = (mohalla) => {
  const idx = MOHALLA_ORDER.findIndex(m => m.toUpperCase() === (mohalla || '').toUpperCase());
  return idx >= 0 ? idx : 999;
};

const DEFAULT_COLUMNS = [
  { id: 'sr_no', label: 'Sr No', default: true },
  { id: 'its_id', label: 'ITS ID', default: true },
  { id: 'full_name', label: 'Name', default: true },
  { id: 'mobile', label: 'Mobile', default: true },
  { id: 'age', label: 'Age', default: false },
  { id: 'gender', label: 'Gender', default: false },
  { id: 'blood_group', label: 'Blood Group', default: false },
  { id: 'member_type', label: 'HOF/FM', default: false },
  { id: 'misaq_status', label: 'Misaq', default: false },
  { id: 'mohalla', label: 'Mohalla', default: true },
  { id: 'sector', label: 'Sector', default: true },
];

export function FilterMembersDialog({ open, onOpenChange, api }) {
  const [step, setStep] = useState(1); // 1: Filter, 2: Results, 3: Export Options
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filter states
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedMohalla, setSelectedMohalla] = useState('all');
  const [selectedSector, setSelectedSector] = useState('all');
  const [memberType, setMemberType] = useState('all'); // all, hof, fm
  const [gender, setGender] = useState('all');
  const [bloodGroup, setBloodGroup] = useState('all');
  const [misaqStatus, setMisaqStatus] = useState('all');
  const [maritalStatus, setMaritalStatus] = useState('all');
  const [occupation, setOccupation] = useState('all');
  const [subOccupation, setSubOccupation] = useState('all');
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [includeParents, setIncludeParents] = useState(false); // NEW: Include parents option
  
  // Results
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [parentMembers, setParentMembers] = useState([]); // NEW: Parent members
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 20;
  
  // Export options
  const [exportFormat, setExportFormat] = useState('excel');
  const [sortBy, setSortBy] = useState('mohalla');
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_COLUMNS.filter(c => c.default).map(c => c.id));
  const [customColumns, setCustomColumns] = useState([]);
  const [newCustomColumn, setNewCustomColumn] = useState('');
  const [separateSheetsBy, setSeparateSheetsBy] = useState('none'); // 'none', 'mohalla', 'sector'

  // Fetch mohallas and sectors on mount
  useEffect(() => {
    if (open) {
      fetchMohallas();
      fetchSectors();
      resetFilters();
    }
  }, [open]);

  const resetFilters = () => {
    setStep(1);
    setSelectedMohalla('all');
    setSelectedSector('all');
    setMemberType('all');
    setGender('all');
    setBloodGroup('all');
    setMisaqStatus('all');
    setMaritalStatus('all');
    setOccupation('all');
    setSubOccupation('all');
    setMinAge('');
    setMaxAge('');
    setIncludeParents(false);
    setFilteredMembers([]);
    setParentMembers([]);
    setTotalCount(0);
    setPage(1);
    setExportFormat('excel');
    setSortBy('mohalla');
    setSelectedColumns(DEFAULT_COLUMNS.filter(c => c.default).map(c => c.id));
    setCustomColumns([]);
    setNewCustomColumn('');
    setSeparateSheetsBy('none');
  };

  const fetchMohallas = async () => {
    try {
      const res = await api.get('/mohallas');
      setMohallas(res.data.mohallas || []);
    } catch (e) {
      console.error('Failed to fetch mohallas');
    }
  };

  const fetchSectors = async () => {
    try {
      const res = await api.get('/sectors');
      setSectors(res.data.sectors || []);
    } catch (e) {
      console.error('Failed to fetch sectors');
    }
  };

  // Filter sectors based on selected mohalla
  const filteredSectors = selectedMohalla === 'all' 
    ? sectors 
    : sectors.filter(s => s.mohalla === selectedMohalla);

  const handleApplyFilters = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('limit', '5000'); // Get all matching records
      
      if (selectedMohalla !== 'all') params.append('mohalla', selectedMohalla);
      if (selectedSector !== 'all') params.append('sector', selectedSector);
      if (gender !== 'all') params.append('gender', gender);
      if (bloodGroup !== 'all') params.append('blood_group', bloodGroup);
      if (misaqStatus !== 'all') params.append('misaq_status', misaqStatus);
      if (maritalStatus !== 'all') params.append('marital_status', maritalStatus);
      if (occupation !== 'all') params.append('occupation', occupation);
      if (subOccupation !== 'all') params.append('sub_occupation', subOccupation);
      if (minAge) params.append('min_age', minAge);
      if (maxAge) params.append('max_age', maxAge);
      if (memberType !== 'all') params.append('member_type', memberType);
      if (includeParents) params.append('include_parents', 'true');
      
      const response = await api.get(`/members/filter?${params.toString()}`);
      let members = response.data.members || [];
      let parents = response.data.parents || [];
      
      // Sort members
      const sortMembers = (list) => {
        if (sortBy === 'mohalla') {
          list.sort((a, b) => {
            const mComp = getMohallaSortKey(a.mohalla) - getMohallaSortKey(b.mohalla);
            if (mComp !== 0) return mComp;
            return (a.sector || '').localeCompare(b.sector || '');
          });
        } else if (sortBy === 'sector') {
          list.sort((a, b) => (a.sector || '').localeCompare(b.sector || ''));
        } else {
          list.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
        }
        return list;
      };
      
      members = sortMembers(members);
      parents = sortMembers(parents);
      
      setFilteredMembers(members);
      setParentMembers(parents);
      setTotalCount(members.length);
      setPage(1);
      setStep(2);
      
      if (members.length === 0) {
        toast.info('No members found matching the criteria');
      } else {
        let message = `Found ${members.length} members`;
        if (parents.length > 0) {
          message += ` and ${parents.length} parents`;
        }
        toast.success(message);
      }
    } catch (error) {
      console.error('Filter error:', error);
      toast.error('Failed to filter members');
    } finally {
      setLoading(false);
    }
  };

  const addCustomColumn = () => {
    if (newCustomColumn.trim()) {
      setCustomColumns([...customColumns, { id: `custom_${Date.now()}`, label: newCustomColumn.trim() }]);
      setNewCustomColumn('');
    }
  };

  const removeCustomColumn = (id) => {
    setCustomColumns(customColumns.filter(c => c.id !== id));
  };

  const toggleColumn = (colId) => {
    setSelectedColumns(prev => 
      prev.includes(colId) ? prev.filter(id => id !== colId) : [...prev, colId]
    );
  };

  const handleExport = async () => {
    if (filteredMembers.length === 0) {
      toast.error('No data to export');
      return;
    }

    setExporting(true);
    try {
      // Helper function to format member data for export
      const formatMemberRow = (member, idx, isParent = false) => {
        const row = {};
        
        // Add selected columns
        selectedColumns.forEach(colId => {
          switch (colId) {
            case 'sr_no':
              row['Sr No'] = idx + 1;
              break;
            case 'its_id':
              row['ITS ID'] = member.its_id || '';
              break;
            case 'full_name':
              row['Name'] = member.full_name || '';
              break;
            case 'mobile':
              row['Mobile'] = member.mobile || '';
              break;
            case 'age':
              row['Age'] = member.age || '';
              break;
            case 'gender':
              row['Gender'] = member.gender || '';
              break;
            case 'blood_group':
              row['Blood Group'] = member.blood_group || '';
              break;
            case 'member_type':
              row['HOF/FM'] = (member.is_hof || member.relationship === 'Self') ? 'HOF' : 'FM';
              break;
            case 'misaq_status':
              row['Misaq'] = member.misaq_status || '';
              break;
            case 'mohalla':
              row['Mohalla'] = member.mohalla || '';
              break;
            case 'sector':
              row['Sector'] = member.sector || '';
              break;
          }
        });
        
        // Add parent-specific columns if this is a parent
        if (isParent) {
          // Determine parent type
          const parentType = member.parent_type || 
            (member.gender?.toLowerCase() === 'female' ? 'Mother' : 'Father');
          row['Parent Type'] = parentType;
          row['Parent Of'] = member.parent_of || '';
        }
        
        // Add custom columns (empty for user to fill)
        customColumns.forEach(col => {
          row[col.label] = '';
        });
        
        return row;
      };

      // Prepare export data for filtered members
      const exportData = filteredMembers.map((member, idx) => formatMemberRow(member, idx, false));

      // Prepare export data for parents (if any)
      const parentsExportData = parentMembers.map((parent, idx) => formatMemberRow(parent, idx, true));

      // Build filter description for filename
      const filterParts = [];
      if (selectedMohalla !== 'all') filterParts.push(selectedMohalla.replace(' MOHALLA', ''));
      if (selectedSector !== 'all') filterParts.push(selectedSector);
      if (memberType !== 'all') filterParts.push(memberType.toUpperCase());
      if (gender !== 'all') filterParts.push(gender);
      if (minAge || maxAge) filterParts.push(`AGE_${minAge || 0}-${maxAge || 100}`);
      
      const filterDesc = filterParts.length > 0 ? filterParts.join('_') : 'ALL';
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `MEMBERS_${filterDesc}_${timestamp}`;

      if (exportFormat === 'excel') {
        // Call backend to generate Excel
        const response = await api.post('/export/filtered-members', {
          members: exportData,
          parents: parentsExportData,
          format: 'excel',
          filename: filename,
          custom_columns: customColumns.map(c => c.label),
          separate_sheets_by: separateSheetsBy === 'none' ? null : separateSheetsBy,
          include_parents: parentMembers.length > 0
        }, { responseType: 'blob' });

        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        const sheetsMsg = separateSheetsBy !== 'none' ? ` (${separateSheetsBy}-wise sheets)` : '';
        let excelMsg = `Excel exported: ${filteredMembers.length} members${sheetsMsg}`;
        if (parentMembers.length > 0) {
          excelMsg += ` + ${parentMembers.length} parents (separate sheet)`;
        }
        toast.success(excelMsg);
      } else {
        // Call backend to generate PDF
        const response = await api.post('/export/filtered-members', {
          members: exportData,
          parents: parentsExportData,
          format: 'pdf',
          filename: filename,
          custom_columns: customColumns.map(c => c.label),
          filter_description: filterDesc,
          include_parents: parentMembers.length > 0
        }, { responseType: 'blob' });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        let pdfMsg = `PDF exported: ${filteredMembers.length} members`;
        if (parentMembers.length > 0) {
          pdfMsg += ` and ${parentMembers.length} parents`;
        }
        toast.success(pdfMsg);
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  // Paginated members for display
  const paginatedMembers = filteredMembers.slice((page - 1) * limit, page * limit);
  const totalPages = Math.ceil(filteredMembers.length / limit);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-navy" />
            Filter & Export Members
            {step > 1 && (
              <Badge className="ml-2 bg-navy">{totalCount} members</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Filter Criteria */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Select criteria to filter members/HOFs</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {/* Mohalla */}
              <div>
                <Label className="text-xs font-medium">Mohalla</Label>
                <Select value={selectedMohalla} onValueChange={(v) => { setSelectedMohalla(v); setSelectedSector('all'); }}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Mohallas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Mohallas</SelectItem>
                    {mohallas.map(m => (
                      <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sector */}
              <div>
                <Label className="text-xs font-medium">Sector</Label>
                <Select value={selectedSector} onValueChange={setSelectedSector}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Sectors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sectors</SelectItem>
                    {filteredSectors.map(s => (
                      <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Member Type */}
              <div>
                <Label className="text-xs font-medium">Member Type</Label>
                <Select value={memberType} onValueChange={setMemberType}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="hof">HOF Only</SelectItem>
                    <SelectItem value="fm">Family Members Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Gender */}
              <div>
                <Label className="text-xs font-medium">Gender</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {GENDERS.map(g => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Blood Group */}
              <div>
                <Label className="text-xs font-medium">Blood Group</Label>
                <Select value={bloodGroup} onValueChange={setBloodGroup}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {BLOOD_GROUPS.map(bg => (
                      <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Misaq Status */}
              <div>
                <Label className="text-xs font-medium">Misaq Status</Label>
                <Select value={misaqStatus} onValueChange={setMisaqStatus}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {MISAQ_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Marital Status */}
              <div>
                <Label className="text-xs font-medium">Marital Status</Label>
                <Select value={maritalStatus} onValueChange={setMaritalStatus}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {MARITAL_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Occupation */}
              <div>
                <Label className="text-xs font-medium">Occupation</Label>
                <Select value={occupation} onValueChange={(v) => { setOccupation(v); setSubOccupation('all'); }}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {OCCUPATION_TYPES.map(o => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sub-Occupation */}
              <div>
                <Label className="text-xs font-medium">Sub-Occupation</Label>
                <Select 
                  value={subOccupation} 
                  onValueChange={setSubOccupation}
                  disabled={occupation === 'all' || !SUB_OCCUPATION_TYPES[occupation]?.length}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {(SUB_OCCUPATION_TYPES[occupation] || []).map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Age Range */}
              <div>
                <Label className="text-xs font-medium">Min Age</Label>
                <Input 
                  type="number" 
                  placeholder="0" 
                  value={minAge} 
                  onChange={(e) => setMinAge(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Max Age</Label>
                <Input 
                  type="number" 
                  placeholder="100" 
                  value={maxAge} 
                  onChange={(e) => setMaxAge(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Include Parents Option - Show when age filter is applied */}
            {(minAge || maxAge) && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Checkbox 
                  id="includeParents"
                  checked={includeParents}
                  onCheckedChange={setIncludeParents}
                />
                <div>
                  <Label htmlFor="includeParents" className="text-sm font-medium cursor-pointer">
                    Retrieve Parents (Father & Mother)
                  </Label>
                  <p className="text-xs text-slate-500">
                    Include the parents of filtered members in a separate section
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleApplyFilters} disabled={loading} className="bg-navy hover:bg-navy/90">
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Apply Filters
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Results */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Filters
              </Button>
              <Button onClick={() => setStep(3)} className="bg-navy hover:bg-navy/90">
                <Download className="w-4 h-4 mr-2" /> Export Options
              </Button>
            </div>

            {filteredMembers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No members found matching the criteria</p>
              </div>
            ) : (
              <>
                {/* Filtered Members Table */}
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Filtered Members ({totalCount})
                  </h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Sr</TableHead>
                          <TableHead className="text-xs">ITS</TableHead>
                          <TableHead className="text-xs">Name</TableHead>
                          <TableHead className="text-xs">Age</TableHead>
                          <TableHead className="text-xs">Mobile</TableHead>
                          <TableHead className="text-xs">Mohalla</TableHead>
                          <TableHead className="text-xs">Sector</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedMembers.map((member, idx) => (
                          <TableRow key={member.id || idx}>
                            <TableCell className="text-xs">{(page - 1) * limit + idx + 1}</TableCell>
                            <TableCell className="text-xs font-mono">{member.its_id || '-'}</TableCell>
                            <TableCell className="text-xs font-medium">{member.full_name}</TableCell>
                            <TableCell className="text-xs">{member.age || '-'}</TableCell>
                            <TableCell className="text-xs">{member.mobile || '-'}</TableCell>
                            <TableCell className="text-xs">{member.mohalla || '-'}</TableCell>
                            <TableCell className="text-xs">{member.sector || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-slate-500">
                        Showing {(page - 1) * limit + 1}-{Math.min(page * limit, totalCount)} of {totalCount}
                      </span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Parents Table - Only shown if includeParents was checked */}
                {parentMembers.length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2 text-blue-700">
                      <Users className="w-4 h-4" />
                      Parents of Filtered Members ({parentMembers.length})
                    </h3>
                    <div className="overflow-x-auto border border-blue-200 rounded-lg bg-blue-50/30">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-blue-100/50">
                            <TableHead className="text-xs">Sr</TableHead>
                            <TableHead className="text-xs">ITS</TableHead>
                            <TableHead className="text-xs">Name</TableHead>
                            <TableHead className="text-xs">Type</TableHead>
                            <TableHead className="text-xs">Mobile</TableHead>
                            <TableHead className="text-xs">Parent Of</TableHead>
                            <TableHead className="text-xs">Mohalla</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {parentMembers.slice(0, 100).map((parent, idx) => (
                            <TableRow key={parent.id || idx}>
                              <TableCell className="text-xs">{idx + 1}</TableCell>
                              <TableCell className="text-xs font-mono">{parent.its_id || '-'}</TableCell>
                              <TableCell className="text-xs font-medium">{parent.full_name}</TableCell>
                              <TableCell className="text-xs">
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${parent.parent_type?.includes('Father') ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-pink-50 text-pink-700 border-pink-300'}`}
                                >
                                  {parent.parent_type || (parent.gender?.toLowerCase() === 'female' ? 'Mother' : 'Father')}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs">{parent.mobile || '-'}</TableCell>
                              <TableCell className="text-xs text-slate-600">{parent.parent_of || '-'}</TableCell>
                              <TableCell className="text-xs">{parent.mohalla || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {parentMembers.length > 100 && (
                      <p className="text-xs text-slate-500 mt-1">Showing first 100 of {parentMembers.length} parents</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 3: Export Options */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Results
              </Button>
            </div>

            {/* Export Format */}
            <div>
              <Label className="text-sm font-medium">Export Format</Label>
              <RadioGroup value={exportFormat} onValueChange={setExportFormat} className="flex gap-4 mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="excel" id="exp-excel" />
                  <Label htmlFor="exp-excel" className="flex items-center gap-2 cursor-pointer">
                    <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    Excel
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pdf" id="exp-pdf" />
                  <Label htmlFor="exp-pdf" className="flex items-center gap-2 cursor-pointer">
                    <FileText className="w-4 h-4 text-red-600" />
                    PDF (Landscape)
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Separate Sheets Option (Excel only) */}
            {exportFormat === 'excel' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <Label className="text-sm font-medium text-green-800 flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4" /> Separate Sheets (Excel Only)
                </Label>
                <RadioGroup value={separateSheetsBy} onValueChange={setSeparateSheetsBy} className="flex flex-wrap gap-4 mt-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="none" id="sheet-none" />
                    <Label htmlFor="sheet-none" className="cursor-pointer text-sm">Single Sheet</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="mohalla" id="sheet-mohalla" />
                    <Label htmlFor="sheet-mohalla" className="cursor-pointer text-sm">Separate by Mohalla</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sector" id="sheet-sector" />
                    <Label htmlFor="sheet-sector" className="cursor-pointer text-sm">Separate by Sector</Label>
                  </div>
                </RadioGroup>
                <p className="text-xs text-green-600 mt-1">Creates a separate Excel sheet for each Mohalla or Sector</p>
              </div>
            )}

            {/* Sort By */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4" /> Sort By
              </Label>
              <RadioGroup value={sortBy} onValueChange={setSortBy} className="flex flex-wrap gap-4 mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="mohalla" id="sort-m" />
                  <Label htmlFor="sort-m" className="cursor-pointer text-sm">Mohalla-wise</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="sector" id="sort-s" />
                  <Label htmlFor="sort-s" className="cursor-pointer text-sm">Sector-wise</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="name" id="sort-n" />
                  <Label htmlFor="sort-n" className="cursor-pointer text-sm">Name (A-Z)</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Column Selection */}
            <div>
              <Label className="text-sm font-medium">Select Columns</Label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                {DEFAULT_COLUMNS.map(col => (
                  <div key={col.id} className="flex items-center space-x-2">
                    <Checkbox 
                      id={col.id}
                      checked={selectedColumns.includes(col.id)}
                      onCheckedChange={() => toggleColumn(col.id)}
                    />
                    <Label htmlFor={col.id} className="text-xs cursor-pointer">{col.label}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Columns */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Custom Columns (Empty for user to fill)
              </Label>
              <div className="flex gap-2 mt-2">
                <Input 
                  placeholder="Enter column name (e.g., Attendance, Payment Status)"
                  value={newCustomColumn}
                  onChange={(e) => setNewCustomColumn(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCustomColumn()}
                  className="flex-1"
                />
                <Button onClick={addCustomColumn} variant="outline" size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {customColumns.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {customColumns.map(col => (
                    <Badge key={col.id} variant="secondary" className="flex items-center gap-1">
                      {col.label}
                      <button onClick={() => removeCustomColumn(col.id)} className="ml-1 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Custom columns will be added as empty columns in the export for manual data entry
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleExport} disabled={exporting} className="bg-navy hover:bg-navy/90">
                {exporting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export {exportFormat.toUpperCase()} ({totalCount} members)
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
